import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Unity, useUnityContext } from 'react-unity-webgl';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '@app/stores';
import { generateNameFromAddress } from '@/shared/utils/wallet';
import { LoadingScreen } from '@/features/game';
import { useSeiBalance } from '@/shared/hooks/useSeiBalance';
import { useMusic } from '@/contexts/MusicContext';

export type GameMode = 'pve' | 'pvp';

interface UnityGameModalProps {
  isOpen: boolean;
  gameMode?: GameMode;
  onClose: () => void;
  onBackHome?: () => void;
}

// Unity build assets base URL
// Local: /build (from public folder)
// Production: Cloudflare R2 CDN
const UNITY_BASE_URL = import.meta.env.DEV
  ? '/build'
  : 'https://cdn.sanctumofsorcery.com/build';

// Cache busting version - fixed at build time, only changes when React app is rebuilt
const UNITY_VERSION = __UNITY_BUILD_TIME__;

export const UnityGameModal = ({
  isOpen,
  gameMode = 'pve',
  onClose,
  onBackHome,
}: UnityGameModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadStartTime, setLoadStartTime] = useState(Date.now());
  const accessToken = useAuthStore((state) => state.accessToken);
  const walletAddress = useAuthStore((state) => state.user?.address);
  const user = useAuthStore((state) => state.user);
  const { balance: seiBalance, refetch: refetchSeiBalance } = useSeiBalance();
  const { switchMusic } = useMusic();

  const enhancedUser = user
    ? {
        ...user,
        username: generateNameFromAddress(user.address),
        sei: seiBalance,
      }
    : null;

  // Switch to combat music when modal opens
  useEffect(() => {
    if (isOpen) {
      switchMusic('/music/combat.mp3');
      setIsLoading(true);
      setLoadStartTime(Date.now());
    }

    // Switch back to background music when modal closes
    return () => {
      if (isOpen) {
        switchMusic('/music/background.mp3');
      }
    };
  }, [isOpen, switchMusic]);

  const {
    unityProvider,
    loadingProgression,
    isLoaded,
    sendMessage,
    addEventListener,
    removeEventListener,
  } = useUnityContext({
    loaderUrl: `${UNITY_BASE_URL}/SeiTD.loader.js?v=${UNITY_VERSION}`,
    dataUrl: `${UNITY_BASE_URL}/SeiTD.data?v=${UNITY_VERSION}`,
    frameworkUrl: `${UNITY_BASE_URL}/SeiTD.framework.js?v=${UNITY_VERSION}`,
    codeUrl: `${UNITY_BASE_URL}/SeiTD.wasm?v=${UNITY_VERSION}`,
  });

  useEffect(() => {
    const handleBackHome = async () => {
      // Update all user data
      try {
        await Promise.all([
          refetchSeiBalance(),
          useAuthStore.getState().validateAndUpdateUser(),
        ]);
      } catch (error) {
        console.error(
          '❌ [UnityGameModal] Failed to refresh user data:',
          error
        );
      } finally {
        onBackHome?.();
        onClose();
      }
    };

    addEventListener('BackHome', handleBackHome);

    return () => {
      removeEventListener('BackHome', handleBackHome);
    };
  }, [
    addEventListener,
    removeEventListener,
    onClose,
    onBackHome,
    refetchSeiBalance,
  ]);

  // Handle PVP display names request from Unity
  useEffect(() => {
    const handleRequestDisplayNames = (data: string) => {
      try {
        const { myWallet, opponentWallet } = JSON.parse(data);

        const myDisplayName = generateNameFromAddress(myWallet);
        const opponentDisplayName = generateNameFromAddress(opponentWallet);

        // Send back to Unity
        sendMessage(
          'LoginBridge',
          'ReceiveDisplayNames',
          JSON.stringify({
            myDisplayName,
            opponentDisplayName,
          })
        );
      } catch (error) {
        console.error(
          '[UnityGameModal] Error handling RequestDisplayNames:',
          error
        );
      }
    };

    addEventListener('RequestDisplayNames', handleRequestDisplayNames);

    return () => {
      removeEventListener('RequestDisplayNames', handleRequestDisplayNames);
    };
  }, [addEventListener, removeEventListener, sendMessage]);

  useEffect(() => {
    const sendDataToUnity = async () => {
      if (isLoaded && isOpen) {
        // Calculate how long loading screen has been shown
        const elapsedTime = Date.now() - loadStartTime;
        const minimumLoadTime = 1000; // 1 second
        const remainingTime = Math.max(0, minimumLoadTime - elapsedTime);

        // Wait for remaining time to ensure loading screen shows for at least 1 second
        await new Promise((resolve) => setTimeout(resolve, remainingTime));

        setIsLoading(false);

        // Send data to Unity - user is always authenticated at this point
        if (!accessToken || !walletAddress) return;

        try {
          // 1. Send API Base URL first - Unity needs this before making any API calls
          const apiBaseUrl =
            import.meta.env.VITE_API_BASE_URL ||
            'https://api.sanctumofsorcery.com';
          sendMessage('LoginBridge', 'ApiBaseUrl', apiBaseUrl);

          // 2. Send AccessToken - Unity needs this to call APIs
          sendMessage('LoginBridge', 'AccessToken', accessToken);

          // 3. Send GameMode immediately after AccessToken to trigger scene load
          // Unity will start loading data from API in the new scene
          console.log('[UnityGameModal] Sending GameMode:', gameMode);
          sendMessage('LoginBridge', 'GameMode', gameMode);

          // 4. Wait for scene to fully load before sending other data
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // 5. Send User data after scene is loaded
          // Note: Avatar is now loaded directly in Unity using walletAddress from /api/v1/user/me
          if (enhancedUser) {
            sendMessage('LoginBridge', 'User', JSON.stringify(enhancedUser));
          }
        } catch (error) {
          console.error('[UnityGameModal] Error sending data to Unity:', error);
        }
      }
    };

    sendDataToUnity();
  }, [
    isLoaded,
    accessToken,
    walletAddress,
    sendMessage,
    isOpen,
    loadStartTime,
    gameMode,
  ]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-9999 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/90 hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        {/* Modal Content */}
        <motion.div
          className="relative w-full h-full max-w-(--app-max-width) mx-auto bg-linear-to-b from-indigo-950 via-purple-950 to-indigo-950"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Loading Overlay */}
          {isLoading && (
            <LoadingScreen progress={Math.round(loadingProgression * 100)} />
          )}

          {/* Unity Game Container */}
          <div className="w-full h-full">
            <Unity
              unityProvider={unityProvider}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
              }}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};
